import { X } from 'lucide-react';
import type { AnalyticsFilters } from '../../types/analytics.types';
import type { FilterOptions } from '../../hooks/useAnalytics';

interface ClassificationFiltersProps {
  filters: AnalyticsFilters;
  options: FilterOptions;
  onChange: (filters: AnalyticsFilters) => void;
}

const SELECT_CLS =
  'text-xs border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed';

function formatMonth(ym: string): string {
  const [year, month] = ym.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, 1)));
}

export const ClassificationFilters = ({ filters, options, onChange }: ClassificationFiltersProps) => {
  const isActive = Object.values(filters).some(Boolean);

  const productiveTypes = filters.species
    ? (options.productiveTypesBySpecies[filters.species] ?? [])
    : [];

  const productNames = filters.productLine
    ? (options.productNamesByLine[filters.productLine] ?? [])
    : [];

  const set = <K extends keyof AnalyticsFilters>(key: K, value: string) =>
    onChange({ ...filters, [key]: value || null });

  const clear = () =>
    onChange({ species: null, productiveType: null, country: null, productLine: null, productName: null, month: null });

  return (
    <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
      <span className="text-xs font-semibold text-slate-500 shrink-0">Filter by</span>

      {/* Month */}
      <select
        value={filters.month ?? ''}
        onChange={(e) => set('month', e.target.value)}
        className={SELECT_CLS}
      >
        <option value="">All Months</option>
        {options.months.map((m) => (
          <option key={m} value={m}>{formatMonth(m)}</option>
        ))}
      </select>

      {/* Species → Productive Type */}
      <div className="flex items-center gap-1">
        <select
          value={filters.species ?? ''}
          onChange={(e) => onChange({ ...filters, species: e.target.value || null, productiveType: null })}
          className={SELECT_CLS}
        >
          <option value="">All Species</option>
          {options.species.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {filters.species && productiveTypes.length > 0 && (
          <>
            <span className="text-slate-300 text-xs">›</span>
            <select
              value={filters.productiveType ?? ''}
              onChange={(e) => set('productiveType', e.target.value)}
              className={SELECT_CLS}
            >
              <option value="">All Types</option>
              {productiveTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Product Line → Sub-product */}
      <div className="flex items-center gap-1">
        <select
          value={filters.productLine ?? ''}
          onChange={(e) => onChange({ ...filters, productLine: e.target.value || null, productName: null })}
          className={SELECT_CLS}
        >
          <option value="">All Products</option>
          {options.productLines.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        {filters.productLine && productNames.length > 0 && (
          <>
            <span className="text-slate-300 text-xs">›</span>
            <select
              value={filters.productName ?? ''}
              onChange={(e) => set('productName', e.target.value)}
              className={SELECT_CLS}
            >
              <option value="">All Sub-products</option>
              {productNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Country */}
      <select
        value={filters.country ?? ''}
        onChange={(e) => set('country', e.target.value)}
        className={SELECT_CLS}
      >
        <option value="">All Countries</option>
        {options.countries.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>

      {isActive && (
        <button
          onClick={clear}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 ml-auto"
        >
          <X className="h-3 w-3" />
          Clear filters
        </button>
      )}
    </div>
  );
};
